import { useState } from 'react';
import { TagCompliancePreview, TagComplianceShelf } from '../../../types';

type TagCompliancePreviewPanelProps = {
  preview: TagCompliancePreview | null | undefined;
};

const subtaskBadgeClass = (subtaskType: string | null): string => {
  if (subtaskType === 'missing_tags') {
    return 'bg-red-100 text-red-800';
  }

  if (subtaskType === 'extra_tags') {
    return 'bg-amber-100 text-amber-800';
  }

  return 'bg-gray-100 text-gray-600';
};

const Stat = ({ label, value }: { label: string; value: number | string }) => (
  <div className="bg-white border border-gray-200 rounded px-3 py-2 min-w-[110px]">
    <div className="text-[11px] uppercase tracking-wide text-gray-500">{label}</div>
    <div className="text-lg font-semibold text-gray-900">{value}</div>
  </div>
);

const ShelfRow = ({ shelf }: { shelf: TagComplianceShelf }) => (
  <tr className="border-b border-gray-100 hover:bg-gray-50">
    <td className="px-2 py-1 whitespace-nowrap font-mono text-[11px]">{shelf.tagName}</td>
    <td className="px-2 py-1 text-center">{shelf.mod}</td>
    <td className="px-2 py-1 text-center">{shelf.shelf}</td>
    <td className="px-2 py-1 text-center">{shelf.pogPositions}</td>
    <td className="px-2 py-1 text-center">{shelf.distinctProducts}</td>
    <td className="px-2 py-1 text-center">{shelf.priceTags}</td>
    <td className="px-2 py-1">
      {shelf.subtaskType ? (
        <span
          className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${subtaskBadgeClass(
            shelf.subtaskType
          )}`}
        >
          {shelf.subtaskType} {shelf.count != null ? `× ${shelf.count}` : ''}
        </span>
      ) : (
        <span className="text-gray-400">—</span>
      )}
    </td>
    <td className="px-2 py-1 text-center">{shelf.priceCheck ? '✓' : ''}</td>
    <td className="px-2 py-1 text-center">{shelf.clipstripPresent ? '✓' : ''}</td>
    <td className="px-2 py-1 text-center">{shelf.midshelfCoveredGaps || ''}</td>
    <td className="px-2 py-1 text-center">
      {shelf.orphanPriceTags.length > 0 ? shelf.orphanPriceTags.length : ''}
    </td>
    <td className="px-2 py-1 text-gray-500">{shelf.skipReason ?? ''}</td>
  </tr>
);

export const TagCompliancePreviewPanel = ({ preview }: TagCompliancePreviewPanelProps) => {
  const [showRawJson, setShowRawJson] = useState(false);

  if (!preview) {
    return null;
  }

  const { totals, config, shelves } = preview;

  return (
    <div className="mt-6 border-t border-gray-200 pt-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-medium text-gray-900">Tag Compliance Preview</h3>
        <button
          onClick={() => setShowRawJson((prev) => !prev)}
          className="text-xs text-blue-600 hover:text-blue-800"
        >
          {showRawJson ? 'Hide raw JSON' : 'Show raw JSON'}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <Stat label="Tasks" value={totals.tasks} />
        <Stat label="Subtasks" value={totals.subtasks} />
        <Stat label="Missing tags" value={totals.missingTags} />
        <Stat label="Extra tags" value={totals.extraTags} />
        <Stat label="Price check shelves" value={totals.priceCheckShelves} />
        <Stat label="Skipped" value={Object.keys(totals.skipped ?? {}).length} />
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded p-3 mb-3">
        <div className="text-[11px] uppercase tracking-wide text-gray-500 mb-1">Config</div>
        <pre className="text-[11px] text-gray-700 whitespace-pre-wrap break-all">
          {JSON.stringify(config, null, 2)}
        </pre>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead className="bg-gray-100 text-gray-600">
            <tr>
              <th className="px-2 py-1 text-left">Tag</th>
              <th className="px-2 py-1">Mod</th>
              <th className="px-2 py-1">Shelf</th>
              <th className="px-2 py-1">POG pos</th>
              <th className="px-2 py-1">Distinct</th>
              <th className="px-2 py-1">Price tags</th>
              <th className="px-2 py-1 text-left">Subtask</th>
              <th className="px-2 py-1">Price check</th>
              <th className="px-2 py-1">Clipstrip</th>
              <th className="px-2 py-1">Midshelf gaps</th>
              <th className="px-2 py-1">Orphan tags</th>
              <th className="px-2 py-1 text-left">Skip reason</th>
            </tr>
          </thead>
          <tbody>
            {shelves.map((shelf) => (
              <ShelfRow key={`${shelf.modCoverageId}-${shelf.mod}-${shelf.shelf}`} shelf={shelf} />
            ))}
          </tbody>
        </table>
      </div>

      {showRawJson && (
        <pre className="mt-3 bg-gray-900 text-gray-100 text-[11px] rounded p-3 overflow-auto max-h-96">
          {JSON.stringify(preview, null, 2)}
        </pre>
      )}
    </div>
  );
};
